/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Injectable } from "@angular/core";
import { ServiceService } from "./service.service";
import { PropertyPatch } from "app/models/property-patch";

/*
 * Constants pertaining to the "Order Portal Validation" Service
 */
/** UUID for the "Order Portal Validation" Service Type */
export const DemoOrderValidation_PortalType_Uuid = '018e7acf-18ca-73d1-9427-4052393d2746';
/** Key for the "Get Current State" Function on the "Order Portal Validation" Service */
const FunctionKey_GetCurrentState = "get_current_state";
/** Key for the "Enter Order Information" Function on the "Order Portal Validation" Service */
const FunctionKey_EnterOrderInfo = "enter_order_info";
/** Key for the "Order Name" Parameter for the "Enter Order Information" Function on the "Order Portal Validation" Service */
const FunctionParameterKey_OrderName = "order_name";
/** Key for the "Clear Order" Function on the "Order Portal Validation" Service */
const FunctionKey_ClearOrder = "clear_order";
/** Key for the "Process Order" Function on the "Order Portal Validation" Service */
const FunctionKey_ProcessOrder = "process_order";


/**
 * Service which provides functions used by the "Demo Order Portal Validation" process.
 * 
 * This calls Functions on "Portal Order Validation" Service instances in Edgefinity and
 * returns the results.
 */
@Injectable()
export class DemoOrderValidationService {

  /**
   * Constructor
   * @param serviceService - Service used to interact with Edgefinity Services.  Used to call the 
   * Functions on the "Portal Order Validation" Service instances.
   */
  constructor(
    private serviceService: ServiceService
  ) { }

  /**
   * Calls the "Get Current State" Function on the "Order Portal Validation" Service with the given ID
   * and returns the result.
   */
  public getCurrentState(portalId: number | string): Promise<DemoOrderValidation_ValidationInfoDto> {
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_GetCurrentState, null);
  }

  /**
   * Calls the "Enter Order Information" Function on the "Order Portal Validation" Service with the 
   * given ID using the given "Order Name" as the "Order Name" Parameter value and returns the result.
   */
  public enterOrderInfo(portalId: number | string, orderName: string): Promise<DemoOrderValidation_ValidationInfoDto> {
    const functionData: PropertyPatch[] = [];
    functionData.push({ property: FunctionParameterKey_OrderName, value: orderName } as PropertyPatch);
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_EnterOrderInfo, functionData);
  }

  /**
   * Calls the "Clear Order" Function on the "Order Portal Validation" Service with the given ID and 
   * returns the result.
   */
  public clearOrder(portalId: number | string): Promise<DemoOrderValidation_ValidationInfoDto> {
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearOrder, null);
  }

  /**
   * Calls the "Process Order" Function on the "Order Portal Validation" Service with the given ID.
   */
  public processOrder(portalId: number | string): Promise<void> {
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ProcessOrder, null);
  }

}

/**
 * Enum representing the possible "State" values for the Order Validation Process
 * being performed by the "Order Portal Validation" Services in Edgefinity.
 */
export enum DemoOrderValidation_ValidationStateEnum {
  /**
   * Order Information needs to be entered for the Order / Items
   * to be looked up / validated.
   */
  EnterOrderInfo = 'EnterOrderInfo',

  /**
   * Order Information is being retrieved
   */
  RetrievingOrderInfo = 'RetrievingOrderInfo',

  /**
   * Order Information has been looked up and can have Reads associated to it.
   * Order Validation in process.
   */
  OrderBeingDisplayed = 'OrderBeingDisplayed'
}

/**
 * DTO representing the information for a Line Item in an Order Validation Attempt 
 * being performed by the "Order Portal Validation" Services
 */
export interface DemoOrderValidation_OrderLineDto {
  /** GTIN-14 */
  gtin14: string | null;
  /** GTIN-14's Description */
  description: string | null;
  /** Number of Tags expected */
  expected_quantity: number | null;
  /** Number of Tags that were read / seen */
  current_quantity: number | null;
}

/**
 * DTO representing an Order Validation Attempt being performed by the 
 * "Order Portal Validation" Services
 */
export interface DemoOrderValidation_OrderDto {
  /** Name of the Order being validated against */
  name: string | null;
  /** Line Items containing informaiton for the validation attempt */
  line_items: DemoOrderValidation_OrderLineDto[] | null;
}

/**
 * DTO representing the "State" the "Order Portal Validation" Services
 * are currently in.  This also contains the information on any 
 * Order Validation Attempt being performed by the Service. 
 */
export interface DemoOrderValidation_ValidationInfoDto {
  state: DemoOrderValidation_ValidationStateEnum;
  validation_attempt: DemoOrderValidation_OrderDto | null;
}
